"use client";

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { CorrelationContext, STORAGE_KEYS } from "./types";

const CorrelationContextInternal = createContext<
  CorrelationContext | undefined
>(undefined);

interface CorrelationProviderProps {
  children: ReactNode;
}

// Generate a unique ID using crypto.randomUUID with fallback
function generateUniqueId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return "xxxx-xxxx-4xxx-yxxx-xxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Get or create correlation ID (persistent across sessions)
function getOrCreateCorrelationId(): string {
  if (typeof window === "undefined") return generateUniqueId();

  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CORRELATION_ID);
    if (stored) return stored;

    const newId = generateUniqueId();
    localStorage.setItem(STORAGE_KEYS.CORRELATION_ID, newId);
    return newId;
  } catch (error) {
    // Fallback if localStorage is not available
    console.warn(
      "localStorage not available, using temporary correlation ID:",
      error,
    );
    return generateUniqueId();
  }
}

// Get or create session ID (temporary per browser session)
function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return generateUniqueId();

  try {
    // Use stored session id if present; otherwise generate

    const stored = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
    if (stored) return stored;

    const newId = generateUniqueId();
    sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, newId);
    return newId;
  } catch (error) {
    // Fallback if sessionStorage is not available
    console.warn(
      "sessionStorage not available, using temporary session ID:",
      error,
    );
    return generateUniqueId();
  }
}

export function CorrelationProvider({ children }: CorrelationProviderProps) {
  const [correlationId, setCorrelationId] = useState<string>("");
  const [sessionId, setSessionId] = useState<string>("");
  const [userContext, setUserContext] = useState<{
    userId?: string;
    organizationId?: string;
  }>({});

  // Initialize IDs on mount
  useEffect(() => {
    setCorrelationId(getOrCreateCorrelationId());
    setSessionId(getOrCreateSessionId());
  }, []);

  const refreshCorrelationId = () => {
    const newId = generateUniqueId();
    setCorrelationId(newId);

    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEYS.CORRELATION_ID, newId);
      } catch (error) {
        console.warn("Failed to store correlation ID:", error);
      }
    }
  };

  const refreshSessionId = () => {
    const newId: string = generateUniqueId();
    setSessionId(newId);

    if (typeof window !== "undefined") {
      try {
        sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, newId);
      } catch (error) {
        console.warn("Failed to store session ID:", error);
      }
    }
  };

  const updateUserContext = (userId?: string, organizationId?: string) => {
    setUserContext({ userId, organizationId });
  };

  const getCorrelationIds = () => ({
    correlationId,
    sessionId,
    userId: userContext.userId,
    organizationId: userContext.organizationId,
  });

  const contextValue: CorrelationContext = {
    correlationId,
    sessionId,
    refreshCorrelationId,
    refreshSessionId,
    updateUserContext,
    getCorrelationIds,
  };

  return (
    <CorrelationContextInternal.Provider value={contextValue}>
      {children}
    </CorrelationContextInternal.Provider>
  );
}

export function useCorrelationContext(): CorrelationContext {
  const context = useContext(CorrelationContextInternal);
  if (context === undefined) {
    throw new Error(
      "useCorrelationContext must be used within a CorrelationProvider",
    );
  }
  return context;
}

// Convenience hooks
export function useCorrelationId(): string {
  const { correlationId } = useCorrelationContext();
  return correlationId;
}

export function useSessionId(): string {
  const { sessionId } = useCorrelationContext();
  return sessionId;
}

// Hook to get all correlation IDs for logging
export function useCorrelationIds() {
  const { correlationId, sessionId } = useCorrelationContext();

  return {
    correlationId,
    sessionId,
    // Add more fields as needed
    timestamp: new Date(),
    environment: process.env.NODE_ENV || "development",
    userAgent:
      typeof window !== "undefined" ? window.navigator.userAgent : undefined,
  };
}
